import { customElement, property } from 'lit/decorators.js';
import { DashboardWidget } from './dashboard-widget';
import { getLicenseDataByBranch, LicensePoolData } from '../../common/supplier-license-data';
import { html, PropertyValues } from 'lit';
import { Snippet } from '../../../../webmodule-common/interop/webmodule-interop';
import { tlang } from '../../../../webmodule-common/other/language/lang';

const ApexCharts = (globalThis as any).ApexCharts;

@customElement('active-licenses-widget')
export class ActiveLicensesWidget extends DashboardWidget {
  @property() franchiseeId?: string = '';
  @property({ type: Number }) maxLicenses: number = 0;
  @property({ type: Number }) availableLicenses: number = 0;
  @property({ type: Array }) branchIds?: string[] = [];

  chart: any | null = null;

  protected async refreshData(): Promise<LicensePoolData> {
    const licenseData = await getLicenseDataByBranch(undefined, this.franchiseeId, this.branchIds);
    return licenseData;
  }

  protected firstUpdated(): void {
    const options = {
      series: [this.activeLicenseCount, this.availableLicenses],
      labels: [tlang`%%license-state-active%%`, tlang`%%license-state-available%%`],
      colors: ['#B95765', '#C7CBD0'],
      chart: {
        type: 'donut',
        // Sized to sit within the compact KPI card with breathing room; the
        // larger value overflowed the card body (the donut touched the border).
        height: '100%',
        offsetY: 0
      },
      dataLabels: {
        enabled: false
      },
      tooltip: {
        enabled: false
      },
      plotOptions: {
        expandOnClick: false,
        pie: {
          donut: {
            size: '60%',
            labels: {
              show: true,
              total: {
                show: true,
                showAlways: true,
                label: tlang`%%license-state-active%%`,
                formatter: () => this.getChartLabel()
              },
              name: {
                show: false
              },
              value: {
                show: true
              }
            }
          }
        }
      },
      legend: { show: false },
      states: {
        hover: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        active: {
          filter: {
            type: 'none',
            value: 0
          }
        }
      }
    };
    this.chart = new ApexCharts(this.querySelector('.licenseChart'), options);
    this.chart.render();
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    this.updateData();
  }

  protected updateData(): void {
    this.refreshData().then(results => {
      this.maxLicenses = results.licensesAllocatedToDealer;
      this.availableLicenses = results.licensesAllocatedToDealer - results.licensesUsedByDealer;

      if (this.chart?.series) {
        this.chart.updateSeries([this.activeLicenseCount, this.availableLicenses], true);
      }
    });
  }

  protected getHeader(): Snippet {
    return tlang`Active Licenses`;
  }

  protected getBody(): Snippet {
    return html`
      <div class="container">
        <div class="row chart-widget-data-wrapper">
          <div class="col-6 chart-widget-data">
            <div class="display-5 text-primary">${this.formatCount(this.activeLicenseCount)}</div>
            <div class="display-6 text-secondary">of ${this.formatCount(this.maxLicenses)} Granted</div>
          </div>
          <div class="col-6 chart-widget-chart">
            <div class="licenseChart"></div>
          </div>
        </div>
      </div>
    `;
  }

  /** Returns the count for display, or a localized N/A when it cannot be calculated (e.g. missing data yields NaN). */
  protected formatCount(value: number): string {
    return Number.isFinite(value) ? `${value}` : tlang`N/A`;
  }

  protected get activeLicenseCount(): number {
    return this.maxLicenses - this.availableLicenses;
  }

  protected getChartLabel(): string {
    const result = ((this.activeLicenseCount + Number.EPSILON) / this.maxLicenses) * 100;
    const percentage = Math.round(result);
    if (Number.isFinite(percentage)) {
      return `${percentage}%`;
    }
    return `0%`;
  }
}
